import React, { useEffect, useState } from "react";
import { invoicesAPI } from "../../../api/apiClient";
import useApi from "../../CustomHooks/useApi";
import Loading from "../../../Loading";
import InvoiceDocumentForm from "./InvoiceDocumentForm";
import InvoiceBankTransfersSection from "./InvoiceBankTransfersSection";
import InvoiceDuplicateCandidates from "./InvoiceDuplicateCandidates";
import { useToast } from "../../../Contexts/ToastContext";
import "../../../css/Views/InvoiceDocumentModal.css";

const InvoiceDocumentModal = ({ documentId, onClose, onSaving, onSaved, onSaveError, onSaveBlocked, onRelationsChanged }) => {
    const { callApi, isLoading, error } = useApi();
    const { showToast, clearToast } = useToast();

    const [document, setDocument] = useState(null);
    const [form, setForm] = useState({ issueDate: "", contractor: "", contractorNip: "", documentNumber: "", ksefNumber: "", grossAmount: null, currency: "PLN", notes: "", isUploadedToKonektoSmart: false, isIgnored: false });
    const [validationErrors, setValidationErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isReplacingFile, setIsReplacingFile] = useState(false);
    const [isChangingPaymentConfirmation, setIsChangingPaymentConfirmation] = useState(false);
    const [isExtractingData, setIsExtractingData] = useState(false);
    const [duplicateCandidates, setDuplicateCandidates] = useState([]);
    const [dismissingDuplicateId, setDismissingDuplicateId] = useState(null);
    const [resolvingDuplicate, setResolvingDuplicate] = useState(null);
    const [showReplacementFiles, setShowReplacementFiles] = useState(false);
    const [replacementFiles, setReplacementFiles] = useState([]);
    const [replacementFilesLoading, setReplacementFilesLoading] = useState(false);

    const loadDocument = (onLoaded = null) => {
        callApi(() => invoicesAPI.getById(documentId), (data) => {
            setDocument(data);
            setDuplicateCandidates(Array.isArray(data.duplicateCandidates) ? data.duplicateCandidates : []);
            setForm({ issueDate: data.issueDate?.substring(0, 10) ?? "", contractor: data.contractor ?? "", contractorNip: data.contractorNip ?? "", documentNumber: data.documentNumber ?? "", ksefNumber: data.ksefNumber ?? "", grossAmount: data.grossAmount ?? null, currency: data.currency ?? "PLN", notes: data.notes ?? "", isUploadedToKonektoSmart: data.isUploadedToKonektoSmart ?? false, isIgnored: data.status === 4 });
            onLoaded?.(data);
        });
    };

    useEffect(() => {
        if (documentId == null)
            return;

        setDocument(null);
        setValidationErrors({});
        setDuplicateCandidates([]);
        setDismissingDuplicateId(null);
        setResolvingDuplicate(null);
        setShowReplacementFiles(false);
        setReplacementFiles([]);
        loadDocument();
    }, [documentId]);

    const validateForm = () => {
        if (form.isIgnored) {
            setValidationErrors({});
            return true;
        }

        const errors = {};
        if (!form.issueDate) errors.issueDate = "Data wystawienia jest wymagana.";
        if (!form.contractor?.trim()) errors.contractor = "Kontrahent jest wymagany.";
        if (!form.documentNumber?.trim()) errors.documentNumber = "Numer dokumentu jest wymagany.";
        if (form.grossAmount == null || form.grossAmount < 0) errors.grossAmount = "Wartość brutto nie może być mniejsza od 0.";
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const hasUnsavedChanges = () => {
        if (!document)
            return false;

        return form.issueDate !== (document.issueDate?.substring(0, 10) ?? "") || form.contractor !== (document.contractor ?? "") || form.contractorNip !== (document.contractorNip ?? "") || form.documentNumber !== (document.documentNumber ?? "") || form.ksefNumber !== (document.ksefNumber ?? "") || form.grossAmount !== (document.grossAmount ?? null) || form.currency !== (document.currency ?? "PLN") || form.notes !== (document.notes ?? "") || form.isUploadedToKonektoSmart !== (document.isUploadedToKonektoSmart ?? false) || form.isIgnored !== (document.status === 4);
    };

    const handleClose = () => {
        if (isBusy)
            return;
        if (hasUnsavedChanges() && !window.confirm("Czy na pewno chcesz zamknąć? Niezapisane zmiany zostaną utracone."))
            return;
        onClose();
    };

    const handleSave = () => {
        if (isSaving || !validateForm())
            return;

        setIsSaving(true);
        onSaving?.();
        showToast("Zapisywanie dokumentu...", "processing");

        const dto = {
            issueDate: form.isIgnored ? null : form.issueDate || null,
            contractor: form.isIgnored ? null : form.contractor?.trim() || null,
            contractorNip: form.isIgnored ? null : form.contractorNip?.trim() || null,
            documentNumber: form.isIgnored ? null : form.documentNumber?.trim() || null,
            ksefNumber: form.isIgnored ? null : form.ksefNumber?.trim() || null,
            grossAmount: form.isIgnored ? null : form.grossAmount,
            currency: form.isIgnored ? null : form.currency?.trim() || "PLN",
            notes: form.notes?.trim() || null,
            isUploadedToKonektoSmart: form.isUploadedToKonektoSmart,
            isIgnored: form.isIgnored
        };

        callApi(() => invoicesAPI.update(documentId, dto), (result) => {
            const duplicates = Array.isArray(result?.duplicateCandidates) ? result.duplicateCandidates : [];
            const hasExactDuplicate = duplicates.some((candidate) => candidate.reason === 1 || candidate.reason === "1" || candidate.reason === "SameFileHash");

            if (hasExactDuplicate) {
                clearToast();
                setDuplicateCandidates(duplicates);
                setIsSaving(false);
                onSaveBlocked?.();
                loadDocument();
                return;
            }

            callApi(() => invoicesAPI.getById(documentId), (updatedDocument) => {
                const updatedDuplicates = Array.isArray(updatedDocument.duplicateCandidates) ? updatedDocument.duplicateCandidates : [];
                setIsSaving(false);
                clearToast();
                showToast("Dokument został zapisany.", "success");
                onSaved?.(updatedDocument);

                if (updatedDocument.duplicateStatus === 1) {
                    setDocument(updatedDocument);
                    setDuplicateCandidates(updatedDuplicates);
                    setForm({ issueDate: updatedDocument.issueDate?.substring(0, 10) ?? "", contractor: updatedDocument.contractor ?? "", contractorNip: updatedDocument.contractorNip ?? "", documentNumber: updatedDocument.documentNumber ?? "", ksefNumber: updatedDocument.ksefNumber ?? "", grossAmount: updatedDocument.grossAmount ?? null, currency: updatedDocument.currency ?? "PLN", notes: updatedDocument.notes ?? "", isUploadedToKonektoSmart: updatedDocument.isUploadedToKonektoSmart ?? false, isIgnored: updatedDocument.status === 4 });
                    return;
                }

                onClose();
            }, () => {
                setIsSaving(false);
                clearToast();
                showToast("Nie udało się odświeżyć zapisanego dokumentu.", "error");
                onSaveError?.();
            });
        }, () => {
            setIsSaving(false);
            clearToast();
            showToast("Nie udało się zapisać dokumentu.", "error");
            onSaveError?.();
        });
    };

    const handleDismissDuplicate = (candidateDocumentId) => {
        if (dismissingDuplicateId != null || resolvingDuplicate != null)
            return;

        setDismissingDuplicateId(candidateDocumentId);
        showToast("Zapisywanie decyzji...", "processing");
        callApi(() => invoicesAPI.dismissDuplicate(documentId, candidateDocumentId), () => {
            const remainingCandidates = duplicateCandidates.filter((candidate) => candidate.documentId !== candidateDocumentId);
            setDuplicateCandidates(remainingCandidates);
            setDocument((currentDocument) => currentDocument ? { ...currentDocument, duplicateCandidates: remainingCandidates, duplicateStatus: remainingCandidates.length > 0 ? 1 : 0 } : currentDocument);
            setDismissingDuplicateId(null);
            clearToast();
            showToast("Dokument oznaczono jako inny.", "success");
            onRelationsChanged?.();
        }, () => {
            setDismissingDuplicateId(null);
            clearToast();
            showToast("Nie udało się zapisać decyzji.", "error");
        });
    };

    const handleDeleteDuplicate = (candidateDocumentId) => {
        if (resolvingDuplicate != null || dismissingDuplicateId != null)
            return;
        if (!window.confirm("Czy na pewno chcesz usunąć bieżący plik jako duplikat? Plik zostanie przeniesiony do kosza Google Drive, a jego wpis usunięty z rejestru."))
            return;

        setResolvingDuplicate({ type: "delete", candidateDocumentId });
        showToast("Usuwanie duplikatu...", "processing");
        callApi(() => invoicesAPI.deleteDuplicate(documentId, candidateDocumentId), () => {
            setResolvingDuplicate(null);
            clearToast();
            showToast("Duplikat został usunięty. Plik przeniesiono do kosza Google Drive.", "success");
            onRelationsChanged?.();
            onClose();
        }, () => {
            setResolvingDuplicate(null);
            clearToast();
            showToast("Nie udało się usunąć duplikatu.", "error");
        });
    };

    const handlePromoteDuplicate = (candidateDocumentId, preservePreviousData = true, usePreviousFileName = false) => {
        if (resolvingDuplicate != null || dismissingDuplicateId != null)
            return;

        const confirmation = preservePreviousData
            ? "Bieżący plik zastąpi wcześniejszy. Dane, status i powiązania wcześniejszego dokumentu zostaną zachowane, a jego stary plik zostanie przeniesiony do kosza Google Drive. Kontynuować?"
            : "Bieżący dokument zostanie zachowany dokładnie z jego obecnymi danymi i powiązaniami. Wcześniejszy dokument oraz jego plik zostaną usunięte. Kontynuować?";

        if (!window.confirm(confirmation))
            return;

        const actionType = preservePreviousData ? "promote" : "keep-current";
        setResolvingDuplicate({ type: actionType, candidateDocumentId });
        showToast(preservePreviousData ? "Zastępowanie wcześniejszego pliku..." : "Usuwanie wcześniejszego dokumentu...", "processing");

        const request = preservePreviousData
            ? () => invoicesAPI.promoteDuplicate(documentId, candidateDocumentId, usePreviousFileName)
            : () => invoicesAPI.keepCurrentDuplicate(documentId, candidateDocumentId, usePreviousFileName);

        callApi(request, () => {
            setResolvingDuplicate(null);
            clearToast();
            showToast(preservePreviousData ? "Bieżący plik został ustawiony jako właściwy. Dane i powiązania wcześniejszego dokumentu zachowano." : "Bieżący dokument został zachowany bez przejmowania danych wcześniejszego dokumentu.", "success");
            onRelationsChanged?.();
            onClose();
        }, () => {
            setResolvingDuplicate(null);
            clearToast();
            showToast("Nie udało się rozstrzygnąć duplikatu.", "error");
        });
    };

    const handleFormChange = (newForm) => setForm(newForm);
    const handleTransfersChanged = (onLoaded = null) => loadDocument(() => { onLoaded?.(); onRelationsChanged?.(); });

    const loadReplacementFiles = () => {
        if (replacementFilesLoading)
            return;
        setReplacementFilesLoading(true);
        callApi(() => invoicesAPI.getReplacementFiles(documentId), (data) => {
            setReplacementFiles(Array.isArray(data) ? data : []);
            setReplacementFilesLoading(false);
            setShowReplacementFiles(true);
        }, () => setReplacementFilesLoading(false));
    };

    const handleReplaceFile = (replacementDocumentId) => {
        if (isReplacingFile)
            return;
        if (!window.confirm("Czy na pewno chcesz zastąpić plik tego dokumentu wybranym plikiem?"))
            return;
        setIsReplacingFile(true);
        showToast("Zastępowanie pliku...", "processing");
        callApi(() => invoicesAPI.replaceFile(documentId, replacementDocumentId), () => {
            setShowReplacementFiles(false);
            setReplacementFiles([]);
            loadDocument(() => {
                setIsReplacingFile(false);
                clearToast();
                showToast("Plik został zastąpiony.", "success");
                onRelationsChanged?.();
            });
        }, () => {
            setIsReplacingFile(false);
            clearToast();
            showToast("Nie udało się zastąpić pliku.", "error");
        });
    };

    const handleDelete = () => {
        if (isDeleting)
            return;
        if (!window.confirm("Czy na pewno chcesz usunąć ten dokument z rejestru?"))
            return;
        setIsDeleting(true);
        showToast("Usuwanie wpisu...", "processing");
        callApi(() => invoicesAPI.delete(documentId), () => {
            setIsDeleting(false);
            clearToast();
            showToast("Wpis został usunięty.", "success");
            onRelationsChanged?.();
            onClose();
        }, () => {
            setIsDeleting(false);
            clearToast();
            showToast("Nie udało się usunąć wpisu.", "error");
        });
    };

    const handlePaymentConfirmationChange = (confirmed) => {
        if (isChangingPaymentConfirmation)
            return;
        setIsChangingPaymentConfirmation(true);
        showToast(confirmed ? "Potwierdzanie płatności..." : "Cofanie potwierdzenia płatności...", "processing");
        callApi(() => invoicesAPI.setPaymentConfirmation(documentId, confirmed), () => {
            loadDocument(() => {
                setIsChangingPaymentConfirmation(false);
                clearToast();
                showToast(confirmed ? "Płatność została ręcznie potwierdzona." : "Ręczne potwierdzenie płatności zostało cofnięte.", "success");
                onRelationsChanged?.();
            });
        }, () => {
            setIsChangingPaymentConfirmation(false);
            clearToast();
            showToast(confirmed ? "Nie udało się potwierdzić płatności." : "Nie udało się cofnąć potwierdzenia płatności.", "error");
        });
    };

    const handleExtractData = () => {
        if (isExtractingData)
            return;
        setIsExtractingData(true);
        showToast("Odczytywanie danych z dokumentu...", "processing");
        callApi(() => invoicesAPI.extractData(documentId), (data) => {
            setForm((currentForm) => ({ ...currentForm, issueDate: data.issueDate?.substring(0, 10) ?? currentForm.issueDate, contractor: data.contractor ?? currentForm.contractor, contractorNip: data.contractorNip ?? currentForm.contractorNip, documentNumber: data.documentNumber ?? currentForm.documentNumber, ksefNumber: data.ksefNumber ?? currentForm.ksefNumber, grossAmount: data.grossAmount ?? currentForm.grossAmount, currency: data.currency ?? currentForm.currency }));
            setValidationErrors({});
            setIsExtractingData(false);
            clearToast();
            showToast("Dane zostały odczytane. Sprawdź je przed zapisaniem.", "success");
        }, () => {
            setIsExtractingData(false);
            clearToast();
            showToast("Nie udało się odczytać danych z dokumentu.", "error");
        });
    };

    const openDriveFile = () => document?.googleDriveFileId && window.open(`https://drive.google.com/file/d/${document.googleDriveFileId}/view`, "_blank", "noopener,noreferrer");
    const openDriveFolder = () => document?.sourceFolderId && window.open(`https://drive.google.com/drive/folders/${document.sourceFolderId}`, "_blank", "noopener,noreferrer");
    const formatDriveDate = (value) => value ? new Date(value).toLocaleDateString("pl-PL") : null;
    const isMissingOnDrive = document?.isMissingOnDrive === true;
    const isExactDuplicate = !isMissingOnDrive && document?.duplicateStatus === 2;
    const isPossibleDuplicate = !isMissingOnDrive && document?.duplicateStatus === 1;
    const isBusy = isSaving || isReplacingFile || isDeleting || isChangingPaymentConfirmation || isExtractingData || dismissingDuplicateId != null || resolvingDuplicate != null;

    if (isLoading && !document)
        return <div className="invoice-modal-overlay"><div className="invoice-modal"><Loading text="Ładowanie dokumentu..." /></div></div>;

    if (error && !document)
        return <div className="invoice-modal-overlay"><div className="invoice-modal"><div className="invoice-modal-error">{error.message}</div></div></div>;

    if (!document)
        return null;

    return (
        <div className="invoice-modal-overlay">
            <div className="invoice-modal">
                {isExactDuplicate && <InvoiceDuplicateCandidates document={document} candidates={duplicateCandidates} dismissingCandidateId={null} resolvingDuplicate={resolvingDuplicate} onDismiss={null} onDelete={handleDeleteDuplicate} onPromote={null} mode="exact" />}

                <div className="invoice-modal-content">
                    {isReplacingFile && <Loading text="Zastępowanie pliku..." />}
                    {isPossibleDuplicate && <InvoiceDuplicateCandidates document={document} candidates={duplicateCandidates} dismissingCandidateId={dismissingDuplicateId} resolvingDuplicate={resolvingDuplicate} onDismiss={handleDismissDuplicate} onDelete={handleDeleteDuplicate} onPromote={handlePromoteDuplicate} mode="possible" />}

                    {!isMissingOnDrive && !isExactDuplicate && (
                        <>
                            <div className="invoice-document-box">
                                <div className="invoice-modal-document-header">
                                    <div className="invoice-modal-document-header-info">
                                        <div className="invoice-modal-file-link" onClick={openDriveFile}>{document.fileName}</div>
                                        <div className="invoice-modal-file-meta">
                                            {document.driveCreatedAt && <span>Wrzucono: {formatDriveDate(document.driveCreatedAt)}</span>}
                                            {document.sourceFolderId && <span className="invoice-modal-folder-link" onClick={openDriveFolder}>📁 {document.sourceFolderName || "Folder"} ↗</span>}
                                        </div>
                                    </div>
                                    {!form.isIgnored && <div className={`invoice-ai-action ${isExtractingData ? "invoice-ai-action-disabled" : ""}`} onClick={() => !isExtractingData && handleExtractData()}><span>✨</span><span>{isExtractingData ? "Odczytywanie..." : "Odczytaj dane (OCR/AI)"}</span></div>}
                                </div>
                                <InvoiceDocumentForm form={form} onChange={handleFormChange} validationErrors={validationErrors} isPaymentManuallyConfirmed={document.isPaymentManuallyConfirmed} paymentManuallyConfirmedByUserName={document.paymentManuallyConfirmedByUserName} paymentManuallyConfirmedAt={document.paymentManuallyConfirmedAt} isChangingPaymentConfirmation={isChangingPaymentConfirmation} onPaymentConfirmationChange={handlePaymentConfirmationChange} />
                            </div>
                            {!form.isIgnored && <InvoiceBankTransfersSection document={document} onChanged={handleTransfersChanged} />}
                        </>
                    )}

                    {isMissingOnDrive && !isReplacingFile && (
                        <>
                            <div className="invoice-modal-document-header">
                                <div className="invoice-modal-file-name">{document.fileName}</div>
                                <div className="invoice-modal-file-meta">
                                    {document.driveCreatedAt && <span>{formatDriveDate(document.driveCreatedAt)}</span>}
                                    {document.sourceFolderId && <span className="invoice-modal-folder-link" onClick={openDriveFolder}>📁 {document.sourceFolderName || "Folder"} ↗</span>}
                                </div>
                            </div>
                            <div className="invoice-modal-missing-file">Plik został usunięty z Google Drive.</div>
                        </>
                    )}

                    {showReplacementFiles && !isReplacingFile && (
                        <div className="invoice-replacement-files">
                            <div className="invoice-replacement-header">
                                <div className="invoice-replacement-title">Wybierz nowy plik</div>
                                <div className="invoice-replacement-description">Wyświetlane są nowe pliki z {String(document.accountingMonth).padStart(2, "0")}/{document.accountingYear}, które nie zostały jeszcze opisane w rejestrze.</div>
                            </div>
                            {replacementFiles.length === 0 && <div className="invoice-replacement-empty">Brak nowych plików wymagających wprowadzenia danych.</div>}
                            {replacementFiles.map((file) => (
                                <div key={file.documentId} className="invoice-replacement-file">
                                    <div className="invoice-replacement-file-info">
                                        <div className="invoice-replacement-file-name">📄 {file.fileName}</div>
                                        <div className="invoice-replacement-file-date">Dodano: {file.createdAt ? formatDriveDate(file.createdAt) : "—"}</div>
                                    </div>
                                    <button className={`invoice-modal-action invoice-modal-action-primary ${isReplacingFile ? "invoice-modal-action-disabled" : ""}`} onClick={() => handleReplaceFile(file.documentId)}>Wybierz</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {!isReplacingFile && (
                    <div className={`invoice-modal-footer ${isExactDuplicate ? "invoice-modal-footer-exact" : ""}`}>
                        {!isExactDuplicate && (
                            <div className="invoice-modal-footer-left">
                                {isMissingOnDrive && !showReplacementFiles && <button className={`invoice-modal-action ${replacementFilesLoading || isBusy ? "invoice-modal-action-disabled" : ""}`} onClick={() => !replacementFilesLoading && !isBusy && loadReplacementFiles()}>{replacementFilesLoading ? "Ładowanie..." : "Zastąp plik"}</button>}
                                {isMissingOnDrive && <button className={`invoice-modal-action invoice-modal-action-danger ${isBusy ? "invoice-modal-action-disabled" : ""}`} onClick={() => !isBusy && handleDelete()}>{isDeleting ? "Usuwanie..." : "Usuń wpis"}</button>}
                            </div>
                        )}
                        <div className="invoice-modal-footer-right">
                            <button className={`invoice-modal-action ${isBusy ? "invoice-modal-action-disabled" : ""}`} onClick={handleClose}>{isExactDuplicate ? "Zamknij" : "Anuluj"}</button>
                            {!isMissingOnDrive && !isExactDuplicate && <button className={`invoice-modal-action invoice-modal-action-primary ${isBusy ? "invoice-modal-action-disabled" : ""}`} onClick={() => !isBusy && handleSave()}>{isSaving ? "Zapisywanie..." : "Zapisz"}</button>}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InvoiceDocumentModal;